import type { CountryCode } from 'libphonenumber-js'

import { getCountries, getCountryCallingCode, isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js'

export const COUNTRY_CODES: Array<CountryCode> = getCountries()

export const POPULAR_COUNTRIES: Array<CountryCode> = ['TN', 'DE', 'FR', 'GB', 'IT', 'BE', 'CH', 'AT', 'NL', 'ES', 'DZ', 'LY', 'MA', 'US', 'CA']

export const isCountryCode = (value: unknown): value is CountryCode =>
    typeof value === 'string' && isSupportedCountry(value)

export const dialCode = (country: CountryCode): string => `+${getCountryCallingCode(country)}`

export interface PhoneParts {
    country: CountryCode | ''
    number: string
}

export type PhoneResult =
    | { ok: true, e164: string, country: CountryCode | null }
    | { ok: false, reason: 'missing' | 'country' | 'invalid' }

export const parsePhone = (parts: PhoneParts): PhoneResult => {
    const raw = (parts.number ?? '').trim()

    if (!raw) return { ok: false, reason: 'missing' }

    const international = raw.startsWith('+') || raw.startsWith('00')

    if (!international && !parts.country) return { ok: false, reason: 'country' }

    const text = raw.startsWith('00') ? `+${raw.slice(2)}` : raw
    const parsed = international
        ? parsePhoneNumberFromString(text)
        : parsePhoneNumberFromString(text, parts.country as CountryCode)

    if (!parsed || !parsed.isValid()) return { ok: false, reason: 'invalid' }

    return { ok: true, e164: parsed.number, country: parsed.country ?? null }
}

export const splitPhone = (e164: string | null | undefined): PhoneParts => {
    if (!e164) return { country: '', number: '' }

    const parsed = parsePhoneNumberFromString(e164)

    if (!parsed) return { country: '', number: e164 }

    return { country: parsed.country ?? '', number: parsed.formatNational() }
}

export const formatPhone = (e164: string | null | undefined): string => {
    if (!e164) return ''

    const parsed = parsePhoneNumberFromString(e164)

    return parsed ? parsed.formatInternational() : e164
}

export const normalizeEmail = (value: string): string => value.trim().toLowerCase()

export const isEmail = (value: unknown): value is string =>
    typeof value === 'string' && value.length <= 254 && /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[^\s@<>()[\],;:"]{2,}$/.test(value.trim())

export const isName = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length >= 1 && value.trim().length <= 60 && /^[\p{L}\p{M}' .-]+$/u.test(value.trim())

export const passwordProblem = (value: unknown, minLength = 8): 'missing' | 'short' | 'weak' | null => {
    if (typeof value !== 'string' || !value) return 'missing'
    if (value.length < minLength || value.length > 128) return 'short'
    if (!/[A-Za-zÀ-ɏ]/.test(value) || !/\d/.test(value)) return 'weak'

    return null
}

export const normalizeMtcn = (value: string): string => value.replace(/[\s-]/g, '')

export const isMtcn = (value: unknown): boolean => typeof value === 'string' && /^\d{10}$/.test(normalizeMtcn(value))

export const safeNext = (value: unknown, fallback = '/'): string => {
    if (typeof value !== 'string') return fallback
    if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback
    if (value.startsWith('/api/') || value.startsWith('/admin')) return fallback

    return value
}

export const clampInt = (value: unknown, min: number, max: number, fallback: number): number => {
    const number = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10)

    if (!Number.isFinite(number)) return fallback

    return Math.min(max, Math.max(min, Math.round(number)))
}
